import { Component, ViewChild, ViewChildren, ViewContainerRef, QueryList, Output, EventEmitter, OnInit, AfterViewInit, OnDestroy, Injector, OnChanges, SimpleChanges, Input } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RSSWAResource } from '../rsswa';
import { RsswaService } from '../rsswa.service';
import { Observable, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { FormType, FormStatusEnum } from 'src/app/common/enumerations/enumerations';
import { RSSWAUpdate } from '../store/rsswa/rsswa.action';
import { utils } from 'src/app/modules/libs/utils';

@Component({
  selector: 'rsswa-side-panel',
  templateUrl: './rsswa-side-panel.component.html',
  styleUrls: ['./rsswa-side-panel.component.scss']
})
export class RsswaSidePanelComponent extends BaseComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  public displayedColumns: string[] = ['serialNo', 'affectedSystemsNames', 'statusName'];
  public dataSource = new MatTableDataSource<RSSWAResource>();

  @Input() filter?: string | null;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  @ViewChildren('matrow', { read: ViewContainerRef }) rows!: QueryList<ViewContainerRef>;

  public serialNo!: string;
  selectedRowIndex? = -1;
  selectedIndex = -1;
  showClosed = false;

  public allowNew!: boolean | null;
  public disabled = false;

  maxHeight!: string;

  @Output() rowSelected = new EventEmitter<any>();
  @Output() newDocument = new EventEmitter<any>();
  @Output() requestClear = new EventEmitter<any>();

  rsswa?: RSSWAResource | null;
  rsswas?: RSSWAResource[];
  rsswasFiltered?: RSSWAResource[];
  rsswas$!: Observable<RSSWAResource[]>;
  rsswasSubs!: Subscription;

  constructor(
    private service: RsswaService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngAfterViewInit(): void {
    this.setSort<RSSWAResource>(this.dataSource, this.sort);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      this.applyFilter();
    }
  }

  override ngOnDestroy(): void {
    this.rsswasSubs?.unsubscribe();
  }

  ngOnInit(): void {
    this.loadRSSWAs();
  }

  loadRSSWAs(): void {
    this.rsswas$ = this.store.select(state => state.RSSWA.data);
    this.rsswasSubs = this.rsswas$.subscribe(async data => {
      if (!data?.length) return;
      this.rsswas = data;

      this.createDataSource();

      const openDocument = await this.checkOpenDocument<RSSWAResource>(FormType.RSSWA, data);
      if (openDocument) {
        this.showClosed = openDocument.closed ?? this.showClosed;
        if (openDocument.document.id != this.rsswa?.id) {
          await this.getValues(openDocument.document);
        }
      }
    });
  }

  /**
   * Rebuilds the side table while preserving the active search term after the RSSWA store refreshes.
   */
  createDataSource() {
    const currentFilter = this.filter?.trim().toLowerCase() ?? '';
    const filteredData = this.rsswas?.filter(x =>
      ((x.status !== FormStatusEnum.Canceled && x.status !== FormStatusEnum.Closed) && !this.showClosed) || this.showClosed
    ).sort((a, b) => utils.sort(a.serialNo, b.serialNo, false));

    this.dataSource = new MatTableDataSource(filteredData);
    // Reapply the predicate and filter because selecting an RSSWA replaces the table data source.
    this.createDataSourceSort();
    this.dataSource.filter = currentFilter;
  }

  createDataSourceSort() {
    this.dataSource.filterPredicate = (data: RSSWAResource, filter: string) => {
      const term = filter.trim().toLowerCase();

      // Lookup requester name
      const requester = this.users.find(u => u.id === data.requesterId);
      const requesterName = requester?.name?.toLowerCase() ?? '';

      // Combine searchable fields
      const combined = [
        data.serialNo,
        data.formStatus?.name,
        requesterName,
        data.description,
      ].join(' ').toLowerCase();

      // Handle negative filter with "-" symbol
      if (term.startsWith('-')) {
        const negativeTerm = term.slice(1); // remove the "-"
        return !combined.includes(negativeTerm);
      }

      return combined.includes(term);
    };

    this.setSort<RSSWAResource>(this.dataSource, this.sort);
  }

  applyFilter() {
    this.createDataSource();
    this.dataSource.filter = this.filter?.trim().toLowerCase() ?? '';
  }

  // async getValues(row: RSSWAResource): Promise<void> {
  //   if (!(await this.canDeactivate()) || this.disabled || !row) return;

  //   this.disabled = true;
  //   //this.loading.emit(true);
  //   this.selectedRowIndex = row.id;
  //   this.setOpenDocument(FormType.RSSWA, row.id);

  //   // Only fetch if not retrieved yet or lacks full data (e.g., wfTable)
  //   const needsFetch = !row.wfTable || row.retrievedBy !== this.currentUser?.id;
  //   if (needsFetch) {
  //     const fullRow = (await this.service.getById(row.id).toPromise()) as RSSWAResource;
  //     if (fullRow) {
  //       fullRow.retrievedBy = this.currentUser?.id;
  //       this.rsswa = fullRow;

  //       // Dispatch update only once after fetch
  //       this.store.dispatch(new RSSWAUpdate(fullRow.id, fullRow));
  //     }
  //   } else {
  //     this.rsswa = row;
  //   }

  //   this.rowSelected.emit(this.rsswa);
  //   this.showElement(this.rsswa);

  //   setTimeout(() => {
  //     this.disabled = false;
  //     //this.loading.emit(false);
  //   }, 1000);
  // }

  rowUnselected() {
    this.rsswa = null;
    this.selectedRowIndex = -1;
    this.rowSelected.emit(null);
  }

  async rowClicked(row: RSSWAResource) {
    if (await this.canDeactivate()) {
      if (this.rsswa?.id == row.id) {
        this.rowUnselected();
      }
      else {
        this.getValues(row);
      }
    }
  }

  async getValues(row: RSSWAResource) {
    this.selectedIndex = this.dataSource.data.findIndex(x => x.id === row.id);
    if (row?.id) {
      this.setOpenDocument(FormType.RSSWA, row.id);
      this.rsswa = (await this.service.getById(row.id).toPromise()) as RSSWAResource;
      if (this.rsswa && this.rsswa?.id) {
        this.store.dispatch(new RSSWAUpdate(this.rsswa?.id, this.rsswa));
        this.selectedRowIndex = this.rsswa?.id;
        this.rowSelected.emit(this.rsswa);
        this.showElement(this.selectedIndex, 7);
      }
    }
  }

  // #region TableScroll
  showElement(index: number, height: number) {
    const indexstr = index.toString();
    const row = this.rows?.find(
      (r) => r.element.nativeElement.getAttribute('position') === indexstr
    );
    if (row) {
      const rect = row.element.nativeElement.getBoundingClientRect();
      if (rect.y <= 0 || rect.y + rect.height > height) {
        row.element.nativeElement.scrollIntoView(false, {
          behavior: 'instant',
        });
      }
      return;
    }
    console.log('not found');
  }

  getPosition(row: any): number {
    const i = this.dataSource.data.findIndex(r => {
      return r.id == row.id;
    });
    return i;
  }
  //#endregion

}
